import React, { useEffect, useMemo, useRef, useState } from 'react';
import { renderIconBySlug } from '../../content/icons';
import { getAvatarBackgroundStyle } from '../../utils/item';

export const LoadingOverlay = props => {
  const {
    label = 'Chargement...',
    itemLabel = '',
    avatarAsset = null,
    bannerAsset = null,
    startedAtMs = null,
  } = props;
  const [avatarLoaded, setAvatarLoaded] = useState(false);
  const [bannerLoaded, setBannerLoaded] = useState(false);
  const [elapsedMs, setElapsedMs] = useState(0);
  const startAtRef = useRef(
    Number.isFinite(startedAtMs) ? startedAtMs : Date.now(),
  );

  const avatarBgStyle = useMemo(
    () => getAvatarBackgroundStyle(avatarAsset?.bgColor),
    [avatarAsset?.bgColor],
  );
  const bannerBgStyle = useMemo(
    () => getAvatarBackgroundStyle(bannerAsset?.bgColor),
    [bannerAsset?.bgColor],
  );

  useEffect(() => {
    if (!avatarAsset?.url) {
      setAvatarLoaded(false);
      return;
    }
    let cancelled = false;
    const img = new Image();
    img.onload = () => {
      if (!cancelled) {
        setAvatarLoaded(true);
      }
    };
    img.onerror = () => {
      if (!cancelled) {
        setAvatarLoaded(false);
      }
    };
    img.src = avatarAsset.url;
    return () => {
      cancelled = true;
    };
  }, [avatarAsset?.url]);

  useEffect(() => {
    if (!bannerAsset?.url) {
      setBannerLoaded(false);
      return;
    }
    let cancelled = false;
    const img = new Image();
    img.onload = () => {
      if (!cancelled) {
        setBannerLoaded(true);
      }
    };
    img.onerror = () => {
      if (!cancelled) {
        setBannerLoaded(false);
      }
    };
    img.src = bannerAsset.url;
    return () => {
      cancelled = true;
    };
  }, [bannerAsset?.url]);

  const showLabel = typeof itemLabel === 'string' && itemLabel.trim() !== '';
  const showAvatar =
    Boolean(avatarAsset?.iconSlug) || Boolean(avatarAsset?.url && avatarLoaded);
  const showBanner =
    Boolean(bannerAsset?.iconSlug) || Boolean(bannerAsset?.url && bannerLoaded);
  const avatarIcon = avatarAsset?.iconSlug
    ? renderIconBySlug(avatarAsset.iconSlug, {
        size: 26,
        color: 'var(--contrast)',
      })
    : null;
  const bannerIcon = bannerAsset?.iconSlug
    ? renderIconBySlug(bannerAsset.iconSlug, {
        size: 22,
        color: 'var(--contrast)',
      })
    : null;

  useEffect(() => {
    if (Number.isFinite(startedAtMs)) {
      startAtRef.current = startedAtMs;
      setElapsedMs(Math.max(0, Date.now() - startedAtMs));
    }
  }, [startedAtMs]);

  useEffect(() => {
    const interval = window.setInterval(() => {
      setElapsedMs(prev => {
        const next = Math.max(0, Date.now() - startAtRef.current);
        // Keep 0.1s precision to avoid excessive rerenders.
        const prevRounded = Math.floor(prev / 100);
        const nextRounded = Math.floor(next / 100);
        return prevRounded === nextRounded ? prev : next;
      });
    }, 100);
    return () => {
      window.clearInterval(interval);
    };
  }, []);

  const elapsedSeconds = elapsedMs / 1000;
  const delayedMessage =
    elapsedSeconds >= 20
      ? 'Le chargement est anormalement long. Une erreur est probablement survenue.'
      : elapsedSeconds >= 10
        ? 'Encore quelques secondes, presque prêt.'
        : elapsedSeconds >= 5
          ? 'Chargement des widgets et des dépendances...'
          : elapsedSeconds >= 2
            ? 'Préparation en cours...'
            : null;

  return (
    <div className='fixed inset-0 bg-main bg-opacity-90 backdrop-blur-xl text-contrast z-120 flex items-center justify-center'>
      <div className='absolute inset-0 opacity-50 pointer-events-none'>
        <div className='absolute -top-20 -left-20 w-80 h-80 rounded-full bg-secondary blur-3xl' />
        <div className='absolute -bottom-20 -right-10 w-96 h-96 rounded-full bg-primary blur-3xl' />
      </div>
      <div className='relative z-10 w-320 max-w-screen90 rounded-xl border border-primary border-opacity-25 overflow-hidden bg-main bg-opacity-70 shadow-2xl'>
        <div
          className='h-40 flex items-center justify-center w-full relative overflow-hidden border-b border-primary border-opacity-20'
          style={{
            ...bannerBgStyle,
            ...(showBanner && bannerAsset?.url
              ? {
                  backgroundImage: `url(${bannerAsset.url})`,
                  backgroundPosition: 'center',
                  backgroundSize: 'cover',
                  backgroundRepeat: 'no-repeat',
                }
              : {}),
          }}
        >
          {showBanner && bannerIcon && (
            <div className='absolute inset-0 flex items-center justify-center backdrop-blur-1'>
              {bannerIcon}
            </div>
          )}

          <div
            className='rounded-full border-2 border-main border-opacity-50 shadow-lg overflow-hidden flex items-center justify-center bg-main'
            style={{
              ...avatarBgStyle,
              width: 100,
              height: 100,
              ...(showAvatar && avatarAsset?.url
                ? {
                    backgroundImage: `url(${avatarAsset.url})`,
                    backgroundPosition: 'center',
                    backgroundSize: 'cover',
                    backgroundRepeat: 'no-repeat',
                  }
                : {}),
            }}
          >
            {showAvatar ? avatarIcon : null}
          </div>
        </div>

        <div className='relative flex flex-col items-center gap-05'>
          <div className='w-full text-center mt-1'>
            <div className='uppercase tracking-widest text-3xs text-contrast text-opacity-60'>
              {label}
            </div>
            <div className='text-lg font-condensed uppercase tracking-wide text-contrast text-opacity-90 min-h-28'>
              {showLabel ? itemLabel : null}
            </div>
          </div>

          <div className='mb-3 w-full flex flex-col items-center justify-center gap-05'>
            <div className='w-3 h-3 rounded-full border-2 border-primary border-t-transparent animate-spin' />
            {/* <div className='text-xs font-condensed uppercase tracking-wider text-contrast text-opacity-55'>
              {elapsedLabel}
            </div> */}
            {delayedMessage && (
              <div className='px-1 text-center text-2xs text-contrast text-opacity-55 leading-tight'>
                {delayedMessage}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
